/**
 * AddToCartModal — per-product role-binding modal (Wave 2 of post-PeopleStep
 * rebuild, Bill 2026-05-06).
 *
 * Replaces v3's flat "click-product-to-toggle" with the SPFx canonical
 * per-product modal that bundles four FSR-decidable sections in one canvas:
 *
 *   1. PRIMARY OWNER         — simple tile click, single-select, eligible
 *                              vs ineligible split with reason text per
 *                              `ineligibilityReason()` (SPFx ProductAddModal
 *                              line 68-82 pattern).
 *   2. JOINT OWNERS          — simple tile click, multi-select, max-N
 *                              enforced (SPFx line 245-259).
 *   3. BENEFICIARIES         — chip row → nested modal (Wave 2c will wire
 *                              the nested editor; this scaffold leaves
 *                              the section as a placeholder).
 *   4. OPTIONAL SERVICES /
 *      FEATURES /
 *      CONFIGURATION         — chip-with-affordance pattern driven by
 *                              ProductFeatures × Feature.configSchema
 *                              (Wave 2d will render the chips).
 *
 * Compliance / Documents / Follow-up DO NOT appear in this modal — they
 * are derived state surfaced on Review summary, Pipeline Preview, and
 * Pipeline Live (Bill 2026-05-05 hard rule).
 *
 * Wave 2b scope: scaffold + Primary + Joint sections fully wired.
 *   Beneficiaries + Optional Config are stubs that compile but show
 *   "Coming Wave 2c/2d" placeholders so the modal is end-to-end testable.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { ModalShell, Button, Pill, Banner } from '@/components/primitives';
import { callClassFor, parseFeatureConfigSchema } from '@retail-ops/shared-types';
import type { OdCustomerTotal } from '@retail-ops/shared-types';
import { useApi } from '../hooks/useApi';
import { useConfig } from '@/contexts/ConfigContext';
import type { Product, Feature, ProductFeature } from '@retail-ops/shared-types/config';
import type { CustomerType } from '@retail-ops/shared-types/enums';
import {
  BeneficiaryEditorModal,
  type BenefDraft,
} from './BeneficiaryEditorModal';
import { ServiceToggle, type PickerSourceMap } from './ServiceToggle';
import { applyOdpRegERule } from './odpRegE';
import { CardsForSection } from './CardsForSection';
import { FundingMethodPicker, type FundingMethodKey } from './FundingMethodPicker';
import { formatSsn } from '@/utils/ssn';
import { formatIsoDate } from '../utils/formatting';
import './AddToCartModal.css';

// ── Cart person shape (parsed from cart.peopleJson, mirrors ProductsStep) ──
export interface CartPersonForModal {
  id?: string;
  customerType?: CustomerType | string;
  dob?: string;
  firstName?: string;
  /** Middle name — surfaced on owner tiles so same-named people (dad/son) are distinguishable. */
  middleName?: string;
  lastName?: string;
  /** SSN/TIN — owner tiles show it formatted, matching the People-step tiles. */
  taxId?: string;
  cifId?: string;
  /** Disambiguates the derived 'NonCitizen' type for product eligibility. */
  citizenshipStatus?: string;
  /** Employment status — gates overdraft protection (D4, BACKLOG-1279): OD
   *  requires at least one employed account owner (Bill 2026-07-17). */
  employmentStatus?: string;
}

/** Calculate age from a YYYY-MM-DD DOB. Returns -1 if unparseable. */
function calcAge(dob?: string): number {
  if (!dob || dob.length < 10) return -1;
  const [yStr, mStr, dStr] = dob.split('-');
  const y = parseInt(yStr ?? '', 10);
  const m = parseInt(mStr ?? '', 10);
  const d = parseInt(dStr ?? '', 10);
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return -1;
  const now = new Date();
  let age = now.getFullYear() - y;
  const monthDiff = (now.getMonth() + 1) - m;
  if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < d)) age--;
  return age;
}

// ── Per-person eligibility — returns reason text or null when eligible ─────
// SPFx ProductAddModal lines 26-82 + v3 ProductsStep getDisabledReason()
// per-role age columns. Role determines which constraints to apply.

export type RoleForEligibility = 'primary' | 'joint';

// JHA short-code → long-form CustomerType map. Mirrors
// `utils/normalizeCustomerType.ts` SHORT_TO_LONG. Inlined here so the
// AddToCartModal primitive doesn't depend on a RAO-module utility (kept
// reusable from other modules + tests).
const SHORT_TO_LONG_CT: Record<string, string> = {
  I: 'Individual',
  B: 'Corporation',
  Business: 'Corporation',
  M: 'Minor',
  T: 'RevocableTrust',
  E: 'Estate',
};
function normalizeForEligibility(raw: string | undefined): string | undefined {
  if (!raw) return raw;
  const trimmed = String(raw).trim();
  if (trimmed.length === 0) return undefined;
  if (trimmed.length <= 2) return SHORT_TO_LONG_CT[trimmed] ?? trimmed;
  if (trimmed === 'Business') return 'Corporation';
  return trimmed;
}

/**
 * Returns null when person is eligible for the given role on the product,
 * or a short human-readable reason string ("Age 14 — requires 18+",
 * "Entity type Corporation not allowed", etc.).
 */
export function ineligibilityReason(
  person: CartPersonForModal,
  product: Product,
  role: RoleForEligibility,
): string | null {
  const age = calcAge(person.dob);
  // Defensive normalize — even if a caller forgets to run normalizeCustomerType()
  // at the read seam, this resolves raw JHA short codes ('I' / 'B') to the
  // long-form CustomerType used by allowedCallClasses + customerTypes gates.
  // Bill 2026-05-11 — caught when Dad/Mom showed "Entity type I not allowed
  // as primary" despite parsePeopleArray normalizing upstream.
  const rawCt = normalizeForEligibility(person.customerType);
  // 'NonCitizen' is a UI-side DERIVATION (it drives CIP document rules) — it
  // is not a canonical product-catalog entity type, so no product whitelist
  // can ever contain it and every non-citizen was refused outright (Bill
  // 2026-07-13 Item 11: a Permanent Resident joint owner). For ELIGIBILITY
  // only, resolve it by citizenship class: true Non-Resident Aliens gate as
  // NonResidentAlien; resident non-citizens gate as Individual.
  const ct =
    rawCt === 'NonCitizen'
      ? person.citizenshipStatus === 'Non-Resident Alien'
        ? 'NonResidentAlien'
        : 'Individual'
      : rawCt;
  const personClass = callClassFor(ct);

  // ── allowed call class membership ────────────────────────────────────
  if (role === 'primary') {
    if (
      product.allowedCallClasses &&
      product.allowedCallClasses.length > 0 &&
      !product.allowedCallClasses.includes(personClass)
    ) {
      return `Entity type ${ct ?? '?'} not allowed as primary`;
    }
  } else {
    if (
      product.allowedJointCallClasses &&
      product.allowedJointCallClasses.length > 0 &&
      !product.allowedJointCallClasses.includes(personClass)
    ) {
      return `Entity type ${ct ?? '?'} not allowed as joint`;
    }
  }

  // ── customerTypes whitelist (extra subtype gate beyond call class) ──
  // PRIMARY-ONLY. This whitelist describes the account's PRIMARY account-holder
  // type (e.g. a Minor account has customerTypes=['Minor'] → the primary must be
  // a minor). It must NOT gate the JOINT co-owner: a Minor account's joint owner
  // is the adult custodian, a different party, correctly gated by
  // allowedJointCallClasses + minAgeCoOwner below. Applying it to joints wrongly
  // rejected the adult custodian on Minor Student Checking / Minor Savings with
  // "Requires entity type: Minor" (Bill 2026-07-20, live with Collette).
  if (role === 'primary' && product.customerTypes && product.customerTypes.length > 0) {
    if (
      ct &&
      !(product.customerTypes as ReadonlyArray<string>).includes(ct)
    ) {
      return `Requires entity type: ${(product.customerTypes as ReadonlyArray<string>).join(', ')}`;
    }
  }

  // ── age windows (per-role columns from Wave 1 #2.5 with legacy fallback)
  const minAge = role === 'primary'
    ? (product.minAgePrimary ?? product.minAge ?? null)
    : (product.minAgeCoOwner ?? null);
  const maxAge = role === 'primary'
    ? (product.maxAgePrimary ?? product.maxAge ?? null)
    : (product.maxAgeCoOwner ?? null);

  if (minAge !== null && age >= 0 && age < minAge) {
    return `Age ${age} — requires ${minAge}+`;
  }
  if (maxAge !== null && age >= 0 && age > maxAge) {
    return `Age ${age} — max age ${maxAge}`;
  }

  return null;
}

/** Convenience predicate: true when person is eligible for the role. */
export function isPersonEligible(
  person: CartPersonForModal,
  product: Product,
  role: RoleForEligibility,
): boolean {
  return ineligibilityReason(person, product, role) === null;
}

// ── Modal commit shape ─────────────────────────────────────────────────
// Emits when FSR clicks "Add to Cart". Wave 2e wires this into cart state.

export interface AddToCartCommit {
  product: Product;
  /**
   * Row-store P2 (2026-07-06) — the line-item INSTANCE this commit targets
   * ("li-{uuid}"; legacy lines: instanceId == productCode). Echoed from
   * `initial.instanceId` so ProductsStep routes the commit to the right
   * addLineItem / updateLineItem command. Optional for back-compat with
   * callers that haven't adopted per-instance targeting yet.
   */
  instanceId?: string;
  primaryOwnerId: string;
  jointOwnerIds: ReadonlyArray<string>;
  /** Bare person-id refs (back-compat with the product line). */
  beneficiaryIds: ReadonlyArray<string>;
  /**
   * Full per-product POD/IRA beneficiary roster (name / % / role / relationship
   * / custodian). v0.24.1 — persisted to cart.metadataJson.beneficiaries keyed
   * by line-item INSTANCE ID (row-store P2; legacy keys == productCode) via the
   * setLineItemBeneficiaries replace-set command, so Review + CartSidebar read
   * the same source the FSR entered (closing the capture-but-never-persisted
   * gap). `beneficiaryIds` above is kept as a derived convenience for the
   * product line.
   */
  beneficiaries: ReadonlyArray<BenefDraft>;
  /** Wave 2d will populate. featureCode → typed value. */
  featureValues: Record<string, unknown>;
  /**
   * Bill 2026-05-08 — opening deposit per product (USD), captured in the
   * modal alongside owners. Persists onto the SelectedProduct entry so the
   * Review step + WorkflowEngine read the same value the FSR confirmed.
   * `undefined` when the input is left blank.
   */
  openingDeposit?: number;
  /**
   * Bill 2026-05-08 — debit card design code (CardTypes.Code from
   * `/v3/config/card-types`). Set only when the product allows a debit card
   * AND the FSR toggled on a debit-card-related service feature. Persists
   * onto the SelectedProduct entry so EFTCardAdd reads the same value.
   */
  cardTypeCode?: string;
}

// ── Component ──────────────────────────────────────────────────────────

/**
 * Card design row shape consumed by the in-modal Debit Card Design picker.
 * The wire shape from `/v3/config/card-types` is the EF entity, which carries
 * `cardCategory` even though the SHARED `CardType` interface in
 * `@retail-ops/shared-types` doesn't (yet). We accept a flat in-modal shape
 * here so the parent can map either source without fighting the type system.
 */
export interface CardDesignOption {
  code: string;
  name: string;
  cardCategory?: string; // 'Personal' | 'Business' | 'ATM'
  cardNetwork?: string;
  isActive?: boolean;
  // BACKLOG-0873 — absolute URL to the admin-maintained artwork served by
  // GET /v3/config/card-designs/{code}/image. Undefined → fall back to the
  // bundled PNG by name, then a gradient. Lets admin-uploaded designs render.
  imageUrl?: string;
}

export interface AddToCartModalProps {
  open: boolean;
  product: Product | null;
  people: ReadonlyArray<CartPersonForModal>;
  /** Catalog of all features (config.features) — looked up by code. */
  features: ReadonlyArray<Feature>;
  /** ProductFeature joins for THIS product (already filtered by parent). */
  productFeatures: ReadonlyArray<ProductFeature>;
  /** Picker source catalogs (cardTypes, checkDesigns, cifAccounts, etc.). */
  pickerSources?: PickerSourceMap;
  /** FSR-facing notes per feature code, shown under the service while it is on
   *  (orderChecks: SystemSettings checks.pricingNote — BACKLOG-1668). */
  featureNotes?: Partial<Record<string, string>>;
  /**
   * Active CardTypes catalog (Bill 2026-05-08 — moved from stage-level
   * Debit Card Design panel into this modal). Already-active rows only;
   * the modal filters further by `cardCategory` against the product's
   * customer-flag.
   */
  cardTypes?: ReadonlyArray<CardDesignOption>;
  onClose: () => void;
  onCommit: (commit: AddToCartCommit) => void;
  /**
   * Minimal instance disambiguator shown in the modal title (e.g. "#2") when
   * the product code already exists in the cart — row-store P2. Omitted for
   * single-instance codes so the title stays clean.
   */
  instanceLabel?: string;
  /**
   * Initial state. Row-store P2: ADD mode passes ONLY `instanceId` (a fresh
   * "li-{uuid}") — adding the SAME product code again no longer hydrates an
   * existing line's config. EDIT mode passes the full config of the targeted
   * INSTANCE (plus its instanceId).
   */
  initial?: {
    /** Line-item instance this modal is configuring — echoed on commit. */
    instanceId?: string;
    primaryOwnerId?: string;
    jointOwnerIds?: ReadonlyArray<string>;
    beneficiaryIds?: ReadonlyArray<string>;
    /** v0.24.1 — full roster re-hydrated from cart.metadataJson.beneficiaries (instance-id key; legacy key == code). */
    beneficiaries?: ReadonlyArray<BenefDraft>;
    featureValues?: Record<string, unknown>;
    openingDeposit?: number;
    cardTypeCode?: string;
  };
  /**
   * Beneficiaries already entered on OTHER product lines in this cart, deduped —
   * offered as one-click "reuse" chips so the FSR doesn't re-type the same
   * beneficiary + custodian per product (Bill v0.70.0 walk / BACKLOG-0837).
   */
  reusableBeneficiaries?: ReadonlyArray<BenefDraft>;
  /**
   * Opens the person dialog to add someone to the cart (MacKenzie Mott 2026-10-16; Bill
   * approved). Called from "+ Add joint owner" when nobody in the cart can be a joint owner.
   */
  onAddPerson?: () => void;
}

/**
 * Joint-owner limit when the product has none configured — matches the server default
 * (AdminProductsService: MaxJointOwners ?? 4).
 */
export const DEFAULT_MAX_JOINT_OWNERS = 4;

/**
 * The primary to preselect on a NEW line: the one person eligible as primary, when there
 * is exactly one (MacKenzie Mott 2026-10-16 — one person showed as both primary and joint).
 */
export function soleEligiblePrimaryId(
  people: ReadonlyArray<CartPersonForModal>,
  product: Product | null,
): string {
  if (!product) return '';
  const eligible = people.filter((p) => !!p.id && ineligibilityReason(p, product, 'primary') === null);
  return eligible.length === 1 ? (eligible[0]!.id ?? '') : '';
}

export function AddToCartModal(props: AddToCartModalProps): JSX.Element | null {
  const { open, product, people, features, productFeatures, pickerSources, featureNotes, cardTypes, onClose, onCommit, initial, instanceLabel, onAddPerson } = props;

  // Overdraft customer-total lookup (D1, BACKLOG-1276 / Collette): existing OD
  // across ALL the customer's accounts, so (existing + new) can be capped.
  const { client, makeSignal } = useApi();
  const [odTotal, setOdTotal] = useState<OdCustomerTotal | null>(null);
  const [odLoading, setOdLoading] = useState<boolean>(false);
  const [odError, setOdError] = useState<string | null>(null);
  const odFetchedCifRef = useRef<string | null>(null);

  // A NEW line preselects the primary when exactly one person can be primary (2026-10-16).
  // An edit keeps whatever the line has.
  const isNewLine = !initial?.primaryOwnerId && !(initial?.jointOwnerIds && initial.jointOwnerIds.length > 0);
  const [primaryOwnerId, setPrimaryOwnerId] = useState<string>(
    () => initial?.primaryOwnerId ?? (isNewLine ? soleEligiblePrimaryId(people, product) : ''),
  );
  // People load after the modal can open — preselect once when the sole candidate appears.
  const primaryAutoPickedRef = useRef<boolean>(false);
  useEffect(() => {
    if (!isNewLine || primaryOwnerId !== '' || primaryAutoPickedRef.current) return;
    const only = soleEligiblePrimaryId(people, product);
    if (only) {
      primaryAutoPickedRef.current = true;
      setPrimaryOwnerId(only);
    }
  }, [isNewLine, primaryOwnerId, people, product]);
  // "+ Add joint owner" expands the list of people who can be joint owners.
  const [jointPickerOpen, setJointPickerOpen] = useState<boolean>(false);
  const [jointOwnerIds, setJointOwnerIds] = useState<ReadonlyArray<string>>(
    initial?.jointOwnerIds ?? [],
  );
  const [beneficiaries, setBeneficiaries] = useState<ReadonlyArray<BenefDraft>>(
    initial?.beneficiaries ?? [],
  );
  const [benefModalOpen, setBenefModalOpen] = useState<boolean>(false);
  const [featureValues, setFeatureValues] = useState<Record<string, unknown>>(
    initial?.featureValues ?? {},
  );
  // Bill 2026-05-08 — Opening Deposit + Card Design moved from stage panels
  // into this modal (cart-shopping doctrine: ALL account-level decisions
  // live here). Default the deposit to '' (string-typed input so users can
  // clear it back to empty); default the card type to the product's
  // catalog-configured cardTypeCode if any.
  const [openingDepositInput, setOpeningDepositInput] = useState<string>(
    typeof initial?.openingDeposit === 'number' ? String(initial.openingDeposit) : '',
  );
  // Bill 2026-05-12 — funding method + deferred-deadline persist on
  // featureValues since SelectedProduct doesn't have those columns yet
  // (shared-types migration deferred to next pass).
  const [fundingMethod, setFundingMethod] = useState<FundingMethodKey | undefined>(() => {
    const v = initial?.featureValues?.['fundingMethod'];
    if (typeof v === 'string' && (
      v === 'internal-transfer' || v === 'cash-check' || v === 'deferred' ||
      v === 'ach' || v === 'debit-card'
    )) return v;
    return undefined;
  });
  const [fundingDeadline, setFundingDeadline] = useState<string>(() => {
    const v = initial?.featureValues?.['fundingDeadline'];
    return typeof v === 'string' ? v : '';
  });
  const [cardTypeCode, setCardTypeCode] = useState<string>(
    initial?.cardTypeCode ?? product?.cardTypeCode ?? '',
  );
  // Defaults filled for the officer to review (Bill 2026-10-14): the flags drive a brief highlight
  // and a note; any edit by the officer clears them.
  const [amountAutofilled, setAmountAutofilled] = useState(false);
  const [deadlineAutofilled, setDeadlineAutofilled] = useState(false);
  const { config: appConfig } = useConfig();

  // Eligibility partitions ──────────────────────────────────────────────
  // For PRIMARY: every person gets a tile; ineligible go to a separate row.
  // For JOINT: anyone NOT the current primary gets a tile (also split).
  const primaryPartition = useMemo(() => {
    if (!product) return { eligible: [], ineligible: [] };
    const eligible: CartPersonForModal[] = [];
    const ineligible: Array<{ person: CartPersonForModal; reason: string }> = [];
    for (const p of people) {
      const reason = ineligibilityReason(p, product, 'primary');
      if (reason) ineligible.push({ person: p, reason });
      else eligible.push(p);
    }
    return { eligible, ineligible };
  }, [people, product]);

  const jointPartition = useMemo(() => {
    if (!product) return { eligible: [], ineligible: [] };
    const eligible: CartPersonForModal[] = [];
    const ineligible: Array<{ person: CartPersonForModal; reason: string }> = [];
    for (const p of people) {
      if (p.id === primaryOwnerId) continue; // primary cannot also be joint on same product
      const reason = ineligibilityReason(p, product, 'joint');
      if (reason) ineligible.push({ person: p, reason });
      else eligible.push(p);
    }
    return { eligible, ineligible };
  }, [people, product, primaryOwnerId]);

  // ── Beneficiary chip pool — exclude primary + joints (Wave 2c dynamic re-filter)
  const availableBenefChips = useMemo(() => {
    return people
      .filter((p) => p.id && p.id !== primaryOwnerId && !jointOwnerIds.includes(p.id))
      .map((p) => ({
        id: p.id ?? '',
        firstName: p.firstName,
        // Carry middle name so the from-cart beneficiary quick-add populates it
        // (Bill 2026-07-22 — was dropped). Phone isn't on CartPersonForModal, so
        // the from-cart path leaves it for CustInq enrichment / manual entry.
        middleName: p.middleName,
        lastName: p.lastName,
        cifId: p.cifId,
        dob: p.dob,
      }));
  }, [people, primaryOwnerId, jointOwnerIds]);

  // ── Configurable features (Wave 2d + Bill 2026-05-12 Services lock) ──
  // Two-stage filter:
  //   1. ProductFeature.state ∈ {Available, Recommended} (Required is silent;
  //      Unavailable is hidden) — admin-config behavior per (product × service).
  //   2. CANONICAL 5 services only (Bill 2026-05-12 lock per
  //      feedback_services_list_canonical_cleanup_20260512.md): Debit Card /
  //      Order Checks / E-Statements / Internet Banking / Overdraft Protection.
  //      Any other feature code (the old 10-item list — Activate Card, Print
  //      Card, Insta-Issue, standalone Reg E, POD Beneficiary Designation,
  //      Overdraft Protection Form) is filtered out at the seam — the FSR
  //      never sees them in the modal, regardless of admin-config state.
  //      Sub-configs (card design, check qty, Reg E opt-in/out nested inside
  //      OD Protection) live inside each ServiceToggle, not as siblings.
  const isCanonicalService = (code: string): boolean => {
    // Normalize to lowercase alphanumerics ONLY, so the camelCase Feature.Code
    // values the DB actually stores (debitCard / orderChecks / electronicStatements /
    // internetBanking / overdraftProtection) AND any legacy hyphen/underscore forms
    // (debit-card / order_checks) collapse to the same token and match.
    // BUG (Bill 2026-06-25 smoke test — no debit/OD/Reg E/checks tiles): the old
    // `.replace(/_/g,'-')` only handled underscores, so camelCase codes like
    // 'debitCard' became 'debitcard' and never equalled 'debit-card' — EVERY
    // service was filtered out and configurableFeatures was always empty.
    const c = code.toLowerCase().replace(/[^a-z0-9]/g, '');
    return (
      c === 'debitcard' || c === 'debit' ||
      c === 'orderchecks' || c === 'checks' ||
      c === 'estatements' || c === 'electronicstatements' || c === 'estmt' || c === 'estmts' ||
      c === 'internetbanking' || c === 'ib' ||
      c === 'overdraftprotection' || c === 'odprotection' || c === 'od' || c === 'overdraft'
    );
  };
  const configurableFeatures = useMemo(() => {
    const featureByCode = new Map(features.map((f) => [f.code, f]));
    return productFeatures
      // Bill 2026-07-17: Required MUST render too — it is auto-selected + LOCKED
      // (can't uncheck), not silent/hidden. Hiding Required was why Debit Card
      // (state=Required on Ultimate Checking) never appeared and the card-design
      // section stayed collapsed. Recommended/Available render unlocked.
      .filter((pf) => pf.state === 'Required' || pf.state === 'Recommended' || pf.state === 'Available')
      .filter((pf) => isCanonicalService(pf.featureCode))
      .map((pf) => ({ pf, feature: featureByCode.get(pf.featureCode) }))
      .filter((row): row is { pf: ProductFeature; feature: Feature } => row.feature != null);
  }, [features, productFeatures]);

  // Default-on for Recommended features the first time the modal opens for a
  // NEW line. The comment below dates to Wave 2d but nothing ever actually
  // seeded the values (Bill 2026-07-13 Item 13: "none of the admin configured
  // per product defaults are wired") — this effect is the wiring. Seeds once
  // per open; never for an edit (initial.featureValues present) and never
  // re-applied after the FSR deselects.
  const defaultsSeededRef = useRef<string | null>(null);
  useEffect(() => {
    if (!open || !product) return;
    const openKey = `${product.code}:${instanceLabel ?? ''}`;
    if (defaultsSeededRef.current === openKey) return;
    defaultsSeededRef.current = openKey;
    const hasInitial =
      initial?.featureValues && Object.keys(initial.featureValues).length > 0;
    if (hasInitial) return;
    const seed: Record<string, unknown> = {};
    for (const { pf, feature } of configurableFeatures) {
      // Auto-select Required AND Recommended (Bill 2026-07-17): both default ON
      // for the primary + joint owners; Required additionally locks (below).
      if (pf.state !== 'Recommended' && pf.state !== 'Required') continue;
      // Shape-aware default (mirrors ServiceToggle.toggle()): compound seeds
      // its field defaults; currency/integer seed defaultValue; pickers stay
      // unseeded (FSR must choose); plain booleans seed true.
      const schema = parseFeatureConfigSchema(feature.configSchema ?? null);
      switch (schema?.type) {
        case 'compound': {
          const v: Record<string, unknown> = {};
          for (const f of schema.fields) {
            if (f.type === 'currency' || f.type === 'integer') v[f.key] = f.defaultValue ?? 0;
            // Pickers now honor a string defaultValue (Bill 2026-07-13 r2 /
            // BACKLOG-0872: Reg E must default to Opted-in when OD is selected).
            // regEOptIn has an explicit fallback so opt-in is the default even
            // before the schema seed carries defaultValue.
            else if (f.type === 'picker' && typeof f.defaultValue === 'string') v[f.key] = f.defaultValue;
            else if (f.type === 'picker' && f.key === 'regEOptIn') v[f.key] = 'optIn';
            else v[f.key] = null;
          }
          // Reg E only while overdraft protection is enrolled (MacKenzie Mott 2026-10-16).
          seed[pf.featureCode] = applyOdpRegERule(schema, v);
          break;
        }
        case 'currency':
        case 'integer':
          seed[pf.featureCode] = schema.defaultValue ?? 0;
          break;
        case 'picker':
          break; // FSR must pick — never auto-defaulted
        default:
          seed[pf.featureCode] = true;
      }
    }
    if (Object.keys(seed).length > 0) {
      setFeatureValues((prev) => ({ ...seed, ...prev }));
    }
  }, [open, product, instanceLabel, initial, configurableFeatures]);

  // Default-on for Recommended features the first time the modal opens.
  // Only applied when featureValues hasn't been seeded yet (initial empty).
  // Done lazily in render so the FSR can deselect a recommended feature.
  const toggledOn = useMemo(() => {
    const set = new Set<string>();
    for (const code of Object.keys(featureValues)) {
      const v = featureValues[code];
      if (v != null && v !== false) set.add(code);
    }
    return set;
  }, [featureValues]);

  // ── Opening deposit parsing + minimum-deposit validation (Bill 2026-05-08) ─
  // `product.minimumDeposit` is `number | string | null` on the wire (decimal
  // typed in SQL, surfaces as either depending on serializer mood). Coerce
  // here. Empty input is ALLOWED — the FSR may be expecting a transfer at
  // funding time — but a non-empty value below the minimum surfaces an
  // inline error and blocks commit.
  const minDeposit = useMemo<number | null>(() => {
    const m = product?.minimumDeposit;
    if (m == null) return null;
    if (typeof m === 'number') return Number.isFinite(m) ? m : null;
    const parsed = parseFloat(String(m));
    return Number.isFinite(parsed) ? parsed : null;
  }, [product]);

  // Opening deposit default: once a funding method is chosen and the amount is still empty, fill
  // the product's minimum opening deposit (Products admin grid, Product.MinimumDeposit).
  useEffect(() => {
    if (fundingMethod === undefined || openingDepositInput.trim() !== '') return;
    if (minDeposit === null || minDeposit <= 0) return;
    setOpeningDepositInput(minDeposit.toFixed(2));
    setAmountAutofilled(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fundingMethod, minDeposit]);

  // Deferred funding deadline default: today + the product's funding window from SystemSetting
  // 'funding.deferredDeadlineDays' = {"default": N, "byProduct": {"CODE": N}} (admin panel, System →
  // Settings). Seeded 7 days per Bill's 2026-05-12 funding-methods design (seed 403).
  useEffect(() => {
    if (fundingMethod !== 'deferred' || fundingDeadline !== '' || !product) return;
    const days = deferredDeadlineDays(appConfig.systemSettings as Record<string, unknown> | undefined, product.code);
    if (days === null) return;
    const d = new Date();
    d.setDate(d.getDate() + days);
    const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    setFundingDeadline(iso);
    setDeadlineAutofilled(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fundingMethod, product]);

  const parsedDeposit = useMemo<number | null>(() => {
    const trimmed = openingDepositInput.trim();
    if (trimmed === '') return null;
    const v = parseFloat(trimmed);
    return Number.isFinite(v) && v >= 0 ? v : null;
  }, [openingDepositInput]);

  const depositError = useMemo<string | null>(() => {
    if (!product) return null;
    if (openingDepositInput.trim() === '') return null;
    if (parsedDeposit == null) return 'Enter a valid amount';
    if (minDeposit !== null && parsedDeposit < minDeposit) {
      return `${product.name} requires a minimum deposit of $${minDeposit.toFixed(2).replace(/\.00$/, '')}`;
    }
    return null;
  }, [product, openingDepositInput, parsedDeposit, minDeposit]);

  // ── Card design eligibility + filtered catalog (Bill 2026-05-08) ──────
  // Determine if any toggled-on feature is debit-card-related (matches
  // /debit/i or /card/i in the feature code). Mirrors the legacy heuristic
  // used by the prior CARD DESIGN TBD banner.
  const hasDebitFeatureOn = useMemo<boolean>(() => {
    for (const code of toggledOn) {
      if (/debit|card/i.test(code)) return true;
    }
    return false;
  }, [toggledOn]);

  // Overdraft employment-gate (D4, BACKLOG-1279 / Bill 2026-07-17): OD is
  // unavailable if NO account owner is employed. We BLOCK only when employment
  // is KNOWN for at least one owner AND none is employed — never on merely
  // missing/uncaptured data (unknown != unemployed), to avoid false-blocking.
  // "Employed" = employmentStatus contains "employ" (Employed / Self-Employed)
  // but not "unemploy".
  // Admin switch (Bill 2026-10-29): overdraft.requireEmployedOwner.enabled, default on. Only an
  // explicit "false" turns the rule off; the commit reads the same setting.
  const odEmploymentRuleOn = useMemo<boolean>(
    () => readSettingString(appConfig.systemSettings, 'overdraft.requireEmployedOwner.enabled')?.trim().toLowerCase() !== 'false',
    [appConfig.systemSettings],
  );
  const overdraftBlockedNoEmployment = useMemo<boolean>(() => {
    if (!odEmploymentRuleOn) return false;
    const ownerIds = new Set<string>([primaryOwnerId, ...jointOwnerIds].filter(Boolean));
    const owners = people.filter((p) => p.id != null && ownerIds.has(p.id));
    const isEmployed = (s?: string): boolean => {
      const x = (s ?? '').toLowerCase();
      return x.includes('employ') && !x.includes('unemploy');
    };
    const anyData = owners.some((p) => (p.employmentStatus ?? '').trim() !== '');
    const anyEmployed = owners.some((p) => isEmployed(p.employmentStatus));
    return anyData && !anyEmployed;
  }, [odEmploymentRuleOn, primaryOwnerId, jointOwnerIds, people]);

  // Blocked means OFF, not just greyed out (Bill's smoke test 2026-10-29). Overdraft is a
  // "Recommended" feature, so it is seeded ON when the product is added; the block only disabled
  // the switch, leaving overdraft enrolled with Reg E opted in and no way to turn it off.
  useEffect(() => {
    if (overdraftBlockedNoEmployment && featureValues['overdraftProtection'] !== undefined) {
      setFeatureValues((prev) => {
        const out = { ...prev };
        delete out['overdraftProtection'];
        return out;
      });
    }
  }, [overdraftBlockedNoEmployment, featureValues]);

  // ── Overdraft customer-level $500-total enforcement (D1, BACKLOG-1276) ──────
  // Collette (walkthrough 2026-07-17, TechReq#28): the $500 overdraft cap is
  // per-CUSTOMER across every account they own, not per-account. When OD is
  // selected for an EXISTING customer, read their current OD total (field 162
  // summed across all deposit accounts) and enforce (existing + new) <= cap.
  const odOn = toggledOn.has('overdraftProtection');
  const primaryCif = useMemo<string>(() => {
    const p = people.find((x) => x.id != null && x.id === primaryOwnerId);
    return (p?.cifId ?? '').trim();
  }, [people, primaryOwnerId]);

  // New OD limit the FSR is requesting on THIS account (the odLimit picker code,
  // e.g. "500"). overdraftProtection is a compound feature: { odLimit, regEOptIn }.
  const newOdLimit = useMemo<number>(() => {
    const od = featureValues['overdraftProtection'];
    if (od == null || typeof od !== 'object') return 0;
    const raw = (od as Record<string, unknown>).odLimit;
    const n = typeof raw === 'number' ? raw : typeof raw === 'string' ? Number(raw) : 0;
    return Number.isFinite(n) ? n : 0;
  }, [featureValues]);

  // Fetch the customer OD total once per (OD-on × CIF). New customers (no CIF)
  // and OD-off skip the call — there is no prior total to sum. Fail-OPEN: a
  // lookup error warns but never blocks (we cannot confirm an overage, so we
  // must not hard-stop the account).
  useEffect(() => {
    if (!odOn || !primaryCif) {
      setOdTotal(null);
      setOdError(null);
      setOdLoading(false);
      odFetchedCifRef.current = null;
      return;
    }
    if (odFetchedCifRef.current === primaryCif) return;
    odFetchedCifRef.current = primaryCif;
    let cancelled = false;
    setOdLoading(true);
    setOdError(null);
    void (async () => {
      try {
        const resp = await client.accounts.odCustomerTotal(primaryCif, { signal: makeSignal() });
        if (cancelled) return;
        if (resp.success && resp.data) {
          setOdTotal(resp.data);
        } else {
          setOdTotal(null);
          setOdError(resp.error ?? 'Could not read the customer’s existing overdraft total.');
        }
      } catch (e) {
        if (cancelled) return;
        setOdTotal(null);
        setOdError(e instanceof Error ? e.message : 'Overdraft lookup failed.');
      } finally {
        if (!cancelled) setOdLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [odOn, primaryCif, client, makeSignal]);

  const odProjectedTotal = odTotal ? odTotal.currentTotal + newOdLimit : null;
  // Hard-block only on a COMPLETE read that shows an overage — never on a partial
  // read (a per-account failure could understate the existing total).
  const odExceedsCap = useMemo<boolean>(() => {
    if (!odOn || !odTotal || odTotal.partial) return false;
    return odTotal.currentTotal + newOdLimit > odTotal.cap;
  }, [odOn, odTotal, newOdLimit]);
  const odCapError = odExceedsCap && odTotal
    ? `Exceeds the $${odTotal.cap} customer overdraft cap — existing $${odTotal.currentTotal} across ${odTotal.accounts.length} account(s) + new $${newOdLimit} = $${odTotal.currentTotal + newOdLimit}. Lower this account’s limit to $${Math.max(0, odTotal.remaining)} or less.`
    : null;

  // Personal vs Business product flag — derived from `allowedCallClasses`.
  // Personal AcctClsfCodes per Field 23 catalog: '0' (Personal), '8' (MSA),
  // '12' (USDA Grant), '14' (Indiv Shadow), '26' (Personal DBA / Sole Prop),
  // '30' (Revocable Trust), '31' (QIT), '32' (Donation Short-Term), '6'
  // (UTMA Custodian — fiduciary but cardholder is the custodian individual).
  // Business AcctClsfCodes: '20'/'25' (LLC/Partnership/Corp), '21' (Non-
  // Profit / Unincorp Assoc), '22' (Public Funds), '24' (Irrev/SNT), '28'
  // (Political Campaign). When the product allows both (no
  // allowedCallClasses or both sides), Personal wins for the picker.
  const productCardCategory = useMemo<'Personal' | 'Business'>(() => {
    const allowed = product?.allowedCallClasses;
    if (!allowed || allowed.length === 0) return 'Personal';
    const PERSONAL = new Set(['0', '6', '8', '12', '14', '26', '30', '31', '32']);
    const BUSINESS = new Set(['20', '21', '22', '24', '25', '28']);
    let hasPersonal = false;
    let hasBusiness = false;
    for (const c of allowed) {
      if (PERSONAL.has(c)) hasPersonal = true;
      if (BUSINESS.has(c)) hasBusiness = true;
    }
    if (hasPersonal) return 'Personal';
    if (hasBusiness) return 'Business';
    return 'Personal';
  }, [product]);

  const eligibleCardTypes = useMemo<ReadonlyArray<CardDesignOption>>(() => {
    if (!cardTypes || cardTypes.length === 0) return [];
    // dbo.CardTypes holds TWO different kinds of row, and conflating them broke this:
    //   PROGRAMS — 9 "Personal Debit" (PERCRD), 11 "Business Debit" (BUSCRD). No art.
    //              The card PRODUCT, determined by the account, never chosen here.
    //   DESIGNS  — 101/103/104/105. The artwork the customer actually picks.
    //
    // This used to filter by `cardCategory === productCardCategory`. Every design is
    // categorised Personal, so a BUSINESS product matched only row 11 — and the gallery
    // rendered one art-less gradient tile instead of the four designs. Collette
    // 2026-07-16: "Business Accounts can choose any design."
    //
    // So the design library is category-independent: active rows that carry artwork.
    // (If a business-only DESIGN is ever added, this needs a real eligibility rule
    // rather than the category column — flagged rather than pre-built on a guess.)
    return cardTypes.filter((c) => {
      if (c.isActive === false) return false;
      return !!c.imageUrl;
    });
  }, [cardTypes]);

  // Bill 2026-05-08 demo blocker fix — early-return MUST sit AFTER every
  // hook call. Prior version returned null between two banks of useMemo,
  // which violated the Rules of Hooks: when the modal toggled open the
  // hook count jumped from 7 → 10 mid-render, throwing React #310
  // ("rendered more hooks than during the previous render"). All hooks
  // already tolerate `!product` internally; placing the gate here is
  // safe and removes the conditional-hook trap.
  if (!open || !product) return null;

  const maxJoints = (product.maxJointOwners ?? DEFAULT_MAX_JOINT_OWNERS);
  // Product catalog AllowJoints (V3ConfigFunction products payload): false hides the section.
  const jointsAllowed = product.allowJoints !== false;
  const chosenJoints = jointsAllowed
    ? jointOwnerIds
        .map((id) => people.find((p) => p.id === id))
        .filter((p): p is CartPersonForModal => !!p && p.id !== primaryOwnerId)
    : [];
  // People who could still be added as a joint: eligible, not the primary, not already a joint.
  const jointCandidates = jointPartition.eligible.filter((p) => !jointOwnerIds.includes(p.id ?? ''));
  const openJointPicker = (): void => {
    if (jointCandidates.length === 0) {
      // Nobody in the cart can be a joint owner: go add the person (2026-10-16).
      if (onAddPerson) onAddPerson();
      else setJointPickerOpen(true);
      return;
    }
    setJointPickerOpen(true);
  };

  const toggleJoint = (id: string): void => {
    if (!id) return;
    setJointOwnerIds((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= maxJoints) return prev;
      return [...prev, id];
    });
  };

  // If primary changes, drop them from joint set (a person can't hold both
  // roles on one product). Same applies if a joint is then promoted to primary.
  const setPrimary = (id: string): void => {
    setPrimaryOwnerId(id);
    setJointOwnerIds((prev) => prev.filter((x) => x !== id));
  };

  const canCommit = primaryOwnerId !== '' && depositError === null && odCapError === null;

  const handleCommit = (): void => {
    if (!canCommit || !product) return;
    const showCardDesign = product.allowDebitCard === true && hasDebitFeatureOn;
    // Merge fundingMethod + fundingDeadline into featureValues so the cart
    // persists the funding choice without a shared-types migration. The
    // canonical `openingDeposit` field continues to flow the dollar amount
    // through the standard channel.
    const mergedFeatureValues: Record<string, unknown> = { ...featureValues };
    if (fundingMethod !== undefined) {
      mergedFeatureValues['fundingMethod'] = fundingMethod;
    } else {
      delete mergedFeatureValues['fundingMethod'];
    }
    if (fundingMethod === 'deferred' && fundingDeadline) {
      mergedFeatureValues['fundingDeadline'] = fundingDeadline;
    } else {
      delete mergedFeatureValues['fundingDeadline'];
    }
    // Primary card design (Bill 2026-07-13 r2 / BACKLOG-0873): the legacy
    // top-level cardTypeCode drives the PRIMARY owner's card via the inline
    // AcctAdd hook, so sync it to the primary owner's assigned per-owner design.
    // Additional owners' designs ride in featureValues.debitCardDesigns and are
    // read by the per-owner EFTCardAdd steps server-side.
    const perOwnerDesigns = mergedFeatureValues['debitCardDesigns'] as Record<string, string> | undefined;
    const primaryDesign = perOwnerDesigns?.[primaryOwnerId];
    const effectiveCardTypeCode = primaryDesign ?? cardTypeCode;
    onCommit({
      product,
      // Row-store P2 — echo the instance this modal was opened for so the
      // parent routes to addLineItem (new) vs updateLineItem (edit).
      ...(initial?.instanceId ? { instanceId: initial.instanceId } : {}),
      primaryOwnerId,
      // Never the primary, and none at all when the product does not allow joint owners.
      jointOwnerIds: jointsAllowed ? jointOwnerIds.filter((id) => id !== primaryOwnerId) : [],
      beneficiaryIds: beneficiaries.map((b) => b.id),
      beneficiaries,
      featureValues: mergedFeatureValues,
      ...(parsedDeposit !== null ? { openingDeposit: parsedDeposit } : {}),
      ...(showCardDesign && effectiveCardTypeCode ? { cardTypeCode: effectiveCardTypeCode } : {}),
    });
  };

  // Acct type for beneficiary mechanism (IRA = '60' uses TaxPlnBenfAdd; else POD CustRelAdd).
  const benefAcctType: '10' | '20' | '30' | '60' =
    product.acctType === '60' ? '60'
    : product.acctType === '30' ? '30'
    : product.acctType === '20' ? '20'
    : '10';

  const setFeatureValue = (code: string, next: unknown): void => {
    setFeatureValues((prev) => {
      const out = { ...prev };
      if (next === undefined) delete out[code];
      else out[code] = next;
      return out;
    });
  };

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      title={`Add to Cart — ${product.name}${instanceLabel ? ` (${instanceLabel})` : ''}`}
      size="xl"
      disableBackdropClose
      footer={
        <div className="rao-add2cart__footer">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={!canCommit} onClick={handleCommit}>
            Add to Cart
          </Button>
        </div>
      }
    >
      <div className="rao-add2cart">
        <div className="rao-add2cart__form">
        {people.length === 0 && (
          <Banner tone="warn" title="Add at least one person to the cart first" />
        )}

        {/* ── PRIMARY OWNER ────────────────────────────────────────── */}
        <section className="rao-add2cart__section">
          <header className="rao-add2cart__section-h">PRIMARY OWNER</header>
          {primaryPartition.eligible.length === 0 && primaryPartition.ineligible.length === 0 && (
            <p className="rao-add2cart__empty">No people available.</p>
          )}
          <div className="rao-add2cart__tiles" role="radiogroup" aria-label="Select primary owner">
            {primaryPartition.eligible.map((p) => (
              <PersonTile
                key={p.id ?? p.cifId ?? Math.random().toString(36)}
                person={p}
                selected={p.id === primaryOwnerId}
                onClick={() => setPrimary(p.id ?? '')}
              />
            ))}
          </div>
          {primaryPartition.ineligible.length > 0 && (
            <div className="rao-add2cart__ineligible">
              <header className="rao-add2cart__ineligible-h">Ineligible:</header>
              <div className="rao-add2cart__ineligible-list">
                {primaryPartition.ineligible.map(({ person, reason }) => (
                  <div
                    key={person.id ?? person.cifId ?? Math.random().toString(36)}
                    className="rao-add2cart__ineligible-row"
                  >
                    <span className="rao-add2cart__ineligible-name">
                      {person.firstName} {person.lastName}
                    </span>
                    <span className="rao-add2cart__ineligible-reason">{reason}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>

        {/* ── JOINT OWNERS ─────────────────────────────────────────── */}
        {/* MacKenzie Mott 2026-10-16 (Bill approved): no always-on grid that listed the
            primary again. Chosen joints show as tiles (click to remove); "+ Add joint owner"
            expands the people who can be joint owners, or opens the person dialog when
            nobody in the cart can be. Hidden when the product does not allow joint owners. */}
        {jointsAllowed && (
        <section className="rao-add2cart__section">
          <header className="rao-add2cart__section-h">
            JOINT OWNERS{' '}
            <span className="rao-add2cart__section-sub">
              (optional, up to {maxJoints})
            </span>
          </header>
          {chosenJoints.length > 0 && (
            <div className="rao-add2cart__tiles" aria-label="Joint owners on this account">
              {chosenJoints.map((p) => (
                <PersonTile
                  key={p.id ?? p.cifId ?? Math.random().toString(36)}
                  person={p}
                  selected
                  onClick={() => toggleJoint(p.id ?? '')}
                />
              ))}
            </div>
          )}
          {chosenJoints.length > 0 && (
            <p className="rao-add2cart__empty">Click a joint owner to remove them.</p>
          )}
          {!jointPickerOpen && chosenJoints.length < maxJoints && (
            <div>
              <Button variant="ghost" size="sm" onClick={openJointPicker} disabled={primaryOwnerId === ''}>
                + Add joint owner
              </Button>
              {primaryOwnerId === '' && (
                <span className="rao-add2cart__empty" style={{ marginLeft: 8 }}>
                  Choose the primary owner first.
                </span>
              )}
            </div>
          )}
          {jointPickerOpen && (
            <div className="rao-add2cart__joint-picker">
              {jointCandidates.length > 0 ? (
                <div className="rao-add2cart__tiles" aria-label="People who can be joint owners">
                  {jointCandidates.map((p) => (
                    <PersonTile
                      key={p.id ?? p.cifId ?? Math.random().toString(36)}
                      person={p}
                      selected={false}
                      onClick={() => {
                        toggleJoint(p.id ?? '');
                        setJointPickerOpen(false);
                      }}
                      disabled={chosenJoints.length >= maxJoints}
                    />
                  ))}
                </div>
              ) : (
                <p className="rao-add2cart__empty">Nobody else in this cart can be a joint owner on this product.</p>
              )}
              {jointPartition.ineligible.length > 0 && (
                <div className="rao-add2cart__ineligible">
                  <header className="rao-add2cart__ineligible-h">Can&rsquo;t be a joint owner:</header>
                  <div className="rao-add2cart__ineligible-list">
                    {jointPartition.ineligible.map(({ person, reason }) => (
                      <div
                        key={person.id ?? person.cifId ?? Math.random().toString(36)}
                        className="rao-add2cart__ineligible-row"
                      >
                        <span className="rao-add2cart__ineligible-name">
                          {person.firstName} {person.lastName}
                        </span>
                        <span className="rao-add2cart__ineligible-reason">{reason}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                {onAddPerson && (
                  <Button variant="ghost" size="sm" onClick={onAddPerson}>
                    Add a new person to the cart
                  </Button>
                )}
                <Button variant="ghost" size="sm" onClick={() => setJointPickerOpen(false)}>
                  Close
                </Button>
              </div>
            </div>
          )}
        </section>
        )}

        {/* ── OPENING DEPOSIT (Bill 2026-05-08 — moved from stage panel) ── */}
        <section className="rao-add2cart__section">
          <header className="rao-add2cart__section-h">
            OPENING DEPOSIT — FUNDING METHOD
          </header>
          <FundingMethodPicker
            amount={openingDepositInput}
            onAmountChange={(v) => { setAmountAutofilled(false); setOpeningDepositInput(v); }}
            method={fundingMethod}
            onMethodChange={setFundingMethod}
            deadline={fundingDeadline}
            onDeadlineChange={(v) => { setDeadlineAutofilled(false); setFundingDeadline(v); }}
            minDeposit={minDeposit}
            depositError={depositError}
            amountAutofilled={amountAutofilled}
            deadlineAutofilled={deadlineAutofilled}
          />
        </section>

        {/* ── BENEFICIARIES (Wave 2c) ──────────────────────────────── */}
        {product.allowBeneficiaries && (
          <section className="rao-add2cart__section">
            <header className="rao-add2cart__section-h">
              BENEFICIARIES{' '}
              <span className="rao-add2cart__section-sub">
                {benefAcctType === '60' ? '(IRA — TaxPlnBenfAdd)' : '(POD — CustRelAdd)'}
              </span>
            </header>
            {beneficiaries.length === 0 && availableBenefChips.length === 0 && (
              <p className="rao-add2cart__empty">
                Add joint owners first or open the editor to enter manually.
              </p>
            )}
            {availableBenefChips.length > 0 && beneficiaries.length === 0 && (
              <div className="rao-add2cart__chips-row">
                <span className="rao-add2cart__chips-label">FROM CART</span>
                {availableBenefChips.map((chip) => (
                  <button
                    key={chip.id}
                    type="button"
                    className="rao-add2cart__chip"
                    onClick={() => setBenefModalOpen(true)}
                  >
                    {chip.firstName} {chip.lastName} ({chip.cifId ?? '—'})
                  </button>
                ))}
              </div>
            )}
            {beneficiaries.length > 0 && (
              <div className="rao-add2cart__benef-summary">
                {beneficiaries.map((b) => (
                  <div key={b.id} className="rao-add2cart__benef-tile">
                    <span className="rao-add2cart__benef-name">
                      {b.firstName} {b.lastName}
                    </span>
                    <span className="rao-add2cart__benef-meta">
                      {b.role === 'primary' ? 'Primary' : 'Contingent'}
                      {' · '}
                      {b.percentage}%
                      {b.relationship ? ` · ${b.relationship}` : ''}
                    </span>
                  </div>
                ))}
              </div>
            )}
            <button
              type="button"
              className="rao-add2cart__benef-edit"
              onClick={() => setBenefModalOpen(true)}
            >
              {beneficiaries.length === 0 ? 'Add beneficiaries' : 'Edit beneficiaries'}
            </button>
          </section>
        )}

        {/* ── OPTIONAL SERVICES / FEATURES / CONFIGURATION (Wave 2d) ─ */}
        {configurableFeatures.length > 0 && (
          <section className="rao-add2cart__section">
            <header className="rao-add2cart__section-h">
              OPTIONAL SERVICES / FEATURES / CONFIGURATION
            </header>
            <div className="rao-svc__tiles">
              {configurableFeatures.map(({ pf, feature }) => (
                <ServiceToggle
                  key={feature.code}
                  feature={feature}
                  value={featureValues[feature.code]}
                  toggledOn={toggledOn}
                  pickerSources={pickerSources}
                  note={featureNotes?.[feature.code]}
                  disabled={pf.state === 'Unavailable' || (feature.code === 'overdraftProtection' && overdraftBlockedNoEmployment)}
                  unavailableReason={feature.code === 'overdraftProtection' && overdraftBlockedNoEmployment
                    ? 'Overdraft protection requires an employed account owner.'
                    : undefined}
                  requiredLock={pf.state === 'Required'}
                  onChange={(v) => setFeatureValue(feature.code, v)}
                />
              ))}
            </div>
          </section>
        )}

        {/* ── OVERDRAFT — CUSTOMER $500 TOTAL (D1, BACKLOG-1276 / Collette) ─
            When OD is selected for an EXISTING customer, show their existing OD
            across all accounts + enforce (existing + new) <= the per-customer
            cap. New customers / OD-off render nothing. Fail-open on lookup error. */}
        {odOn && primaryCif && (
          <section className="rao-add2cart__section">
            <header className="rao-add2cart__section-h">OVERDRAFT — CUSTOMER TOTAL</header>
            {odLoading && (
              <Banner tone="info">Checking this customer’s existing overdraft across their accounts…</Banner>
            )}
            {!odLoading && odError && (
              <Banner tone="warn">
                Couldn’t read the existing overdraft total ({odError}). Proceed mindful of the
                per-customer cap — the overdraft across <em>all</em> the customer’s accounts must
                not exceed it.
              </Banner>
            )}
            {!odLoading && odTotal && (
              odCapError ? (
                <Banner tone="err">{odCapError}</Banner>
              ) : (
                <Banner tone={odTotal.partial ? 'warn' : 'info'}>
                  Existing overdraft: <strong>${odTotal.currentTotal}</strong> across{' '}
                  {odTotal.accounts.length} account(s). Cap <strong>${odTotal.cap}</strong> ·
                  remaining <strong>${Math.max(0, odTotal.remaining)}</strong>.
                  {newOdLimit > 0 && (
                    <> This account adds <strong>${newOdLimit}</strong> → total{' '}
                      <strong>${odProjectedTotal}</strong>.</>
                  )}
                  {odTotal.partial && odTotal.note && (
                    <><br /><em>{odTotal.note}</em></>
                  )}
                </Banner>
              )
            )}
          </section>
        )}

        {/* ── CARDS FOR + per-owner DEBIT CARD DESIGN (Bill 2026-07-13 r2 /
            BACKLOG-0873) — each checked owner gets their own card AND their own
            design. Drag a design from the gallery onto an owner (or click a
            design then click an owner) to assign it; a thumbnail shows on the
            owner. Every owner defaults to Code 101. Persisted at
            featureValues.debitCardDesigns { [ownerId]: cardTypeCode }, plus
            debitCardOwners (who gets a card). Card art is inline SVG (no CDN);
            real card images can drop in as cardTypes data URIs later. */}
        {product.allowDebitCard === true && hasDebitFeatureOn && eligibleCardTypes.length > 0 && (
          <CardsForSection
            people={people}
            primaryOwnerId={primaryOwnerId}
            jointOwnerIds={jointOwnerIds}
            eligibleCardTypes={eligibleCardTypes}
            featureValues={featureValues}
            setFeatureValues={setFeatureValues}
            productCardCategory={productCardCategory}
          />
        )}
        {product.allowDebitCard === true && hasDebitFeatureOn && eligibleCardTypes.length === 0 && (
          <section className="rao-add2cart__section">
            <header className="rao-add2cart__section-h">DEBIT CARD DESIGN</header>
            <p className="rao-add2cart__empty">
              No active {productCardCategory.toLowerCase()} card designs in
              <code> /v3/config/card-types</code>. Have an admin seed the catalog before issuing this card.
            </p>
          </section>
        )}
        </div>{/* /.rao-add2cart__form */}

        {/* ── GUIDANCE PANEL (Bill 2026-05-08 — Modal-2-col lock) ───
            Persistent right-rail surfacing product reg refs, locked-field
            defaults, BSA/CIP/CDD reminders driven by the cart's people +
            this product's acctType + the FSR's current selections. */}
        <aside className="rao-add2cart__guide" aria-label="Guidance">
          <GuidancePanel
            product={product}
            people={people}
            primaryOwnerId={primaryOwnerId}
            jointOwnerIds={jointOwnerIds}
            featureValues={featureValues}
            cardTypeCode={cardTypeCode}
            beneficiaryCount={beneficiaries.length}
            hasDebitFeatureOn={hasDebitFeatureOn}
          />
        </aside>
      </div>

      {/* Nested beneficiary editor — opens when FROM CART chip clicked or
          "Add/Edit beneficiaries" button. ModalShell stacks via portal so
          the parent modal stays mounted underneath. */}
      <BeneficiaryEditorModal
        open={benefModalOpen}
        title={`Beneficiaries — ${product.name}`}
        initial={beneficiaries}
        cartPersonChips={availableBenefChips}
        reusableBeneficiaries={props.reusableBeneficiaries ?? []}
        acctType={benefAcctType}
        onClose={() => setBenefModalOpen(false)}
        onCommit={(drafts) => {
          setBeneficiaries(drafts);
          setBenefModalOpen(false);
        }}
      />
    </ModalShell>
  );
}

// ── GuidancePanel — persistent right-rail (Bill 2026-05-08 lock) ──────
// Surfaces product reg refs, locked-field defaults, BSA/CIP/CDD reminders
// driven by what the FSR is doing in the form on the left. Per
// `feedback_modal_two_column_with_guidance_panel_20260508`: every Person
// + Account modal renders 2-col with persistent <aside> guidance right.

interface GuidancePanelProps {
  product: Product;
  people: ReadonlyArray<CartPersonForModal>;
  primaryOwnerId: string;
  jointOwnerIds: ReadonlyArray<string>;
  featureValues: Record<string, unknown>;
  cardTypeCode: string;
  beneficiaryCount: number;
  hasDebitFeatureOn: boolean;
}

function GuidancePanel(props: GuidancePanelProps): JSX.Element {
  const { product, people, primaryOwnerId, jointOwnerIds, featureValues, cardTypeCode, beneficiaryCount, hasDebitFeatureOn } = props;
  const primaryPerson = people.find((p) => p.id === primaryOwnerId);
  const jointCount = jointOwnerIds.length;
  const featureCount = Object.keys(featureValues).filter((k) => featureValues[k] !== undefined && featureValues[k] !== false).length;

  // Reg refs by acctType (CFR citations applicable to the product wire path)
  const regRefs: ReadonlyArray<{ code: string; desc: string }> = (() => {
    const refs: Array<{ code: string; desc: string }> = [];
    if (product.acctType === '10' || product.acctType === '20') {
      refs.push({ code: '12 CFR 1030', desc: 'Reg DD / TISA — APY + fee disclosures required at open' });
      refs.push({ code: '12 CFR 1005', desc: 'Reg E — EFT err-resolve disclosures (if debit/ACH)' });
    }
    if (product.acctType === '30') {
      refs.push({ code: '12 CFR 1030', desc: 'Reg DD — TIS for TimeDep + early-withdrawal pen' });
    }
    if (product.acctType === '60') {
      refs.push({ code: '26 USC 408', desc: 'IRA Custodial — IRS form 5305 family disclosures' });
      refs.push({ code: 'IRS Pub 590-A', desc: 'Contribution + rollover rules' });
    }
    if (product.allowDebitCard === true) {
      refs.push({ code: '12 CFR 1005.7', desc: 'Reg E §1005.7 — initial disclosures at card issue' });
    }
    refs.push({ code: '31 CFR 1020.220', desc: 'BSA CIP Rule — primary + signers fully identity-verified' });
    return refs;
  })();

  return (
    <div className="rao-add2cart__guide-body">
      <header className="rao-add2cart__guide-h">PRODUCT</header>
      <dl className="rao-add2cart__guide-dl">
        <dt>Name</dt><dd>{product.name}</dd>
        <dt>Code</dt><dd>{product.code}</dd>
        <dt>AcctType</dt><dd>{product.acctType}</dd>
        {product.glCode ? (<><dt>GLProdCode</dt><dd>{product.glCode}</dd></>) : null}
      </dl>

      <header className="rao-add2cart__guide-h">CURRENT BINDING</header>
      <dl className="rao-add2cart__guide-dl">
        <dt>Primary</dt>
        <dd>
          {primaryPerson
            ? `${primaryPerson.firstName ?? ''} ${primaryPerson.lastName ?? ''}`.trim()
            : <em className="rao-add2cart__guide-empty">— not selected —</em>}
        </dd>
        <dt>Joint</dt><dd>{jointCount === 0 ? '—' : `${jointCount} added`}</dd>
        {product.allowBeneficiaries && (<><dt>Beneficiaries</dt><dd>{beneficiaryCount === 0 ? '—' : `${beneficiaryCount} added`}</dd></>)}
        {featureCount > 0 ? (<><dt>Features on</dt><dd>{featureCount}</dd></>) : null}
        {hasDebitFeatureOn && cardTypeCode ? (<><dt>Card design</dt><dd>{cardTypeCode}</dd></>) : null}
      </dl>

      <header className="rao-add2cart__guide-h">LOCKED DEFAULTS</header>
      <ul className="rao-add2cart__guide-ul">
        {product.acctType === '10' && <li>Stmt cycle: 11 (monthly)</li>}
        {product.minimumDeposit ? <li>Min opening deposit: ${String(product.minimumDeposit)}</li> : null}
        {product.acctType === '10' && <li>InsiderCode: 0 (Non-Employee, unless FSR overrides)</li>}
        <li>TinCode: derived from primary CIF (S=SSN, E=EIN, Forn=NRA)</li>
        {product.allowDebitCard === false && <li className="rao-add2cart__guide-warn">Debit card: NOT ALLOWED on this product</li>}
      </ul>

      <header className="rao-add2cart__guide-h">REGULATORY</header>
      <ul className="rao-add2cart__guide-ul">
        {regRefs.map((r) => (
          <li key={r.code}><strong>{r.code}</strong> — {r.desc}</li>
        ))}
      </ul>

      {primaryOwnerId === '' && (
        <div className="rao-add2cart__guide-alert">
          <strong>Primary owner required</strong> before commit. The Primary is the CIF that owns
          the account-level CIP/CDD record and receives statements. Joint owners co-sign but are
          not the regulatory owner.
        </div>
      )}
    </div>
  );
}

// ── PersonTile — eligible-tile click target ───────────────────────────

interface PersonTileProps {
  person: CartPersonForModal;
  selected: boolean;
  disabled?: boolean;
  onClick: () => void;
}

/**
 * Single tile for Primary or Joint owner selection. Uses the toggle-button
 * `aria-pressed` pattern (no `role` attribute) so the same component serves
 * both single-select (parent radiogroup) and multi-select contexts. Screen
 * readers announce "pressed" / "not pressed" as the selection state.
 */
function PersonTile({ person, selected, disabled = false, onClick }: PersonTileProps): JSX.Element {
  const initials = `${(person.firstName ?? '?').charAt(0)}${(person.lastName ?? '?').charAt(0)}`.toUpperCase();
  const subtype = person.customerType ?? '—';

  // Identification block (Bill 2026-07-02) — name alone can't distinguish a
  // dad and son with the same first/last. Middle name + DOB + SSN + CIF give
  // the FSR the same anchors the People-step tiles show.
  const displayName = [person.firstName, person.middleName, person.lastName]
    .filter(Boolean).join(' ');
  const dobTxt = person.dob ? `DOB ${formatIsoDate(person.dob)}` : '';
  const age = calcAge(person.dob);
  const idLine = [
    dobTxt ? `${dobTxt}${age >= 0 ? ` (${age})` : ''}` : '',
    person.taxId ? `SSN ${formatSsn(person.taxId)}` : '',
  ].filter(Boolean).join(' · ');
  const cifLine = [
    person.cifId ? `CIF ${person.cifId}` : 'New customer',
    subtype,
  ].filter(Boolean).join(' · ');

  const cls = [
    'rao-add2cart__tile',
    selected ? 'is-selected' : '',
    disabled ? 'is-disabled' : '',
  ].filter(Boolean).join(' ');

  // Accessible label expresses selection state in addition to the visual ✓.
  // Skips aria-pressed since the JSX-a11y linter rejects expression-typed
  // values; the explicit aria-label carries the same semantic for AT users.
  const ariaLabel = [
    displayName,
    dobTxt,
    selected ? 'selected' : '',
  ].filter(Boolean).join(' — ');

  return (
    <button
      type="button"
      aria-label={ariaLabel}
      disabled={disabled}
      onClick={onClick}
      className={cls}
    >
      <span aria-hidden="true" className="rao-add2cart__tile-avatar">{initials}</span>
      <span className="rao-add2cart__tile-body">
        <strong className="rao-add2cart__tile-name">{displayName}</strong>
        {idLine && <span className="rao-add2cart__tile-meta">{idLine}</span>}
        <span className="rao-add2cart__tile-meta">{cifLine}</span>
      </span>
      {selected && <span aria-hidden="true" className="rao-add2cart__tile-check">✓</span>}
    </button>
  );
}

/** Days in the deferred-funding window for a product: byProduct[code], else default; null when
 *  the setting is missing or unreadable (no default is filled then). */
export function deferredDeadlineDays(settings: Record<string, unknown> | undefined, productCode: string): number | null {
  const raw = settings?.['funding.deferredDeadlineDays'];
  let cfg: unknown = raw;
  if (typeof raw === 'string') {
    try { cfg = JSON.parse(raw); } catch { return null; }
  }
  if (!cfg || typeof cfg !== 'object') return null;
  const c = cfg as { default?: unknown; byProduct?: Record<string, unknown> };
  const pick = (v: unknown): number | null =>
    typeof v === 'number' && Number.isInteger(v) && v > 0 && v <= 365 ? v : null;
  return pick(c.byProduct?.[productCode]) ?? pick(c.default);
}

/** A SystemSettings value from the config payload, which arrives as rows ({settingKey,settingValue})
 *  or as a flat key/value map. Undefined when the key is absent. */
function readSettingString(systemSettings: unknown, key: string): string | undefined {
  if (Array.isArray(systemSettings)) {
    for (const row of systemSettings as ReadonlyArray<Record<string, unknown>>) {
      if (!row || typeof row !== 'object') continue;
      const k = row['settingKey'] ?? row['key'];
      if (k === key) {
        const v = row['settingValue'] ?? row['value'];
        return typeof v === 'string' ? v : v == null ? undefined : String(v);
      }
    }
    return undefined;
  }
  if (systemSettings && typeof systemSettings === 'object') {
    const v = (systemSettings as Record<string, unknown>)[key];
    return typeof v === 'string' ? v : v == null ? undefined : String(v);
  }
  return undefined;
}
